import { useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Sparkles, X } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { useSimpleAiJobStore, type SimpleAiJob } from '@/stores/simple-ai-job.store';
import { eduflowSimpleApi } from '@/api/eduflow-simple.api';
import { toast } from '@/components/ui/Toast';
import { emitEvent, onEvent } from '@/lib/socket';

const POLL_MS = 4000;

function jobLabel(job: SimpleAiJob): string {
  if (job.currentChapterLabel) {
    return job.currentChapterLabel;
  }
  if (!job.structureReady) {
    return 'Préparation de la structure…';
  }
  return 'Génération en cours…';
}

export function SimpleAiJobTracker() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const activeJobs = useSimpleAiJobStore((s) => s.activeJobs);
  const setActiveJobs = useSimpleAiJobStore((s) => s.setActiveJobs);
  const upsertJob = useSimpleAiJobStore((s) => s.upsertJob);
  const removeJob = useSimpleAiJobStore((s) => s.removeJob);
  const prevJobIdsRef = useRef<Set<string>>(new Set());

  const poll = useCallback(async () => {
    if (!isAuthenticated) {
      prevJobIdsRef.current = new Set();
      setActiveJobs([]);
      return;
    }
    try {
      const jobs = await eduflowSimpleApi.listActiveAiJobs();
      const nextIds = new Set(jobs.map((j) => j.id));
      for (const prevId of prevJobIdsRef.current) {
        if (!nextIds.has(prevId)) {
          const prevJob = useSimpleAiJobStore.getState().activeJobs.find((j) => j.id === prevId);
          if (prevJob) {
            window.dispatchEvent(
              new CustomEvent('lyrra-simple-ai-job-done', { detail: { flowId: prevJob.flowId } }),
            );
          }
        }
      }
      prevJobIdsRef.current = nextIds;
      setActiveJobs(jobs);
    } catch {
      // ignore transient errors
    }
  }, [isAuthenticated, setActiveJobs]);

  useEffect(() => {
    void poll();
    if (!isAuthenticated) return undefined;
    const id = window.setInterval(() => void poll(), POLL_MS);
    return () => window.clearInterval(id);
  }, [isAuthenticated, poll]);

  useEffect(() => {
    const onRefresh = () => void poll();
    window.addEventListener('lyrra-simple-ai-jobs-refresh', onRefresh);
    return () => window.removeEventListener('lyrra-simple-ai-jobs-refresh', onRefresh);
  }, [poll]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const unsubs = [
      onEvent<SimpleAiJob>('simple-ai:progress', (job) => {
        window.dispatchEvent(new CustomEvent('lyrra-simple-ai-progress', { detail: job }));
      }),
      onEvent<SimpleAiJob>('simple-ai:done', (job) => {
        window.dispatchEvent(new CustomEvent('lyrra-simple-ai-done', { detail: job }));
      }),
      onEvent<SimpleAiJob>('simple-ai:failed', (job) => {
        window.dispatchEvent(new CustomEvent('lyrra-simple-ai-failed', { detail: job }));
      }),
    ];
    return () => {
      unsubs.forEach((unsub) => unsub());
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!user?.id) return;
    for (const job of activeJobs) {
      emitEvent('joinEduFlow', {
        flowId: job.flowId,
        userId: user.id,
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
      });
    }
  }, [activeJobs, user]);

  useEffect(() => {
    const onProgress = (e: Event) => {
      const job = (e as CustomEvent<SimpleAiJob>).detail;
      if (job?.id) upsertJob(job);
    };
    const onDone = (e: Event) => {
      const job = (e as CustomEvent<SimpleAiJob>).detail;
      if (!job?.id) return;
      removeJob(job.id);
      toast.success(`Formation « ${job.flowTitle} » générée par l’IA.`);
      window.dispatchEvent(
        new CustomEvent('lyrra-simple-ai-job-done', { detail: { flowId: job.flowId } }),
      );
      void poll();
    };
    const onFailed = (e: Event) => {
      const job = (e as CustomEvent<SimpleAiJob>).detail;
      if (!job?.id) return;
      removeJob(job.id);
      toast.error(
        job.errorMessage ?? `La génération IA a échoué pour « ${job.flowTitle} ».`,
      );
      window.dispatchEvent(
        new CustomEvent('lyrra-simple-ai-job-failed', { detail: { flowId: job.flowId, message: job.errorMessage } }),
      );
      void poll();
    };

    window.addEventListener('lyrra-simple-ai-progress', onProgress);
    window.addEventListener('lyrra-simple-ai-done', onDone);
    window.addEventListener('lyrra-simple-ai-failed', onFailed);
    return () => {
      window.removeEventListener('lyrra-simple-ai-progress', onProgress);
      window.removeEventListener('lyrra-simple-ai-done', onDone);
      window.removeEventListener('lyrra-simple-ai-failed', onFailed);
    };
  }, [poll, removeJob, upsertJob]);

  return null;
}

export function SimpleAiJobBanner() {
  const activeJobs = useSimpleAiJobStore((s) => s.activeJobs);
  const bannerHiddenJobIds = useSimpleAiJobStore((s) => s.bannerHiddenJobIds);
  const hideBanner = useSimpleAiJobStore((s) => s.hideBanner);
  const visibleJobs = activeJobs.filter((j) => !bannerHiddenJobIds.includes(j.id));
  if (!visibleJobs.length) return null;

  const job = visibleJobs[0]!;
  const progress = Math.min(100, Math.max(0, Math.round(job.progress)));
  const chapterHint =
    job.totalChapters > 0
      ? `${Math.min(job.completedChapters, job.totalChapters)}/${job.totalChapters} chapitre(s)`
      : null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[90] border-t border-violet-200/80 bg-white/95 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-8px_32px_rgba(15,23,42,0.12)] backdrop-blur-sm dark:border-violet-900/50 dark:bg-gray-950/95"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-200">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-900 dark:text-white">
              <Sparkles className="h-3.5 w-3.5 text-violet-600" aria-hidden />
              L’IA génère votre formation
            </p>
            <p className="truncate text-sm text-slate-600 dark:text-slate-300">
              {job.flowTitle}
              {chapterHint ? ` · ${chapterHint}` : ''}
              {job.currentChapterLabel ? ` — ${jobLabel(job)}` : ''}
            </p>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-gray-800">
              <div
                className="h-full rounded-full bg-violet-600 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 self-end sm:self-center">
          <Link
            to={`/eduflow/simple/${job.flowId}/structure`}
            className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700"
          >
            Voir la formation
          </Link>
          <button
            type="button"
            className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-gray-800"
            aria-label="Masquer la notification (la génération continue)"
            onClick={() => hideBanner(job.id)}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
